import axios from 'axios';
import { toast } from 'react-toastify';
import { loginRequest } from '../authConfig';
import store from '../redux/store';
import { setToken } from '../redux/slices/userSlice';
import { msalInstance } from '../index';

export const client = axios.create({
  // baseURL: 'http://localhost:5000/api',
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercept all requests - attach JWT from Redux store
client.interceptors.request.use(
  async (config) => {
    if (!config.headers.Authorization) {
      const state = store.getState();
      const token = state.user?.token;

      if (token && token !== "error") {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    config.headers.version = "1.0.3";
    return config;
  },
  (error) => Promise.reject(error)
);

// Track whether a token refresh is already in progress to prevent parallel retries
let isRefreshing = false;

// Intercept all responses
client.interceptors.response.use(
  async response => {
    return response;
  },
  async (error) => {
    // Network error (server down, no internet, etc.)
    if (!error.response) {
      console.error('[NETWORK ERROR]', error.message);
      toast.error("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    console.log('[API ERROR] CODE:', status, 'MESSAGE:', data?.error || data?.message);

    if (status === 401) {
      const originalRequest = error.config;

      // Don't retry if already retried or if this is the login request itself
      if (originalRequest._retry || originalRequest.url?.includes('/data/login')) {
        store.dispatch(setToken({ token: "error" }));
        return Promise.reject(error);
      }

      // Don't retry if another refresh is already in progress
      if (isRefreshing) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Try MSAL re-authentication
      try {
        const currentAccount = msalInstance.getActiveAccount();
        if (currentAccount) {
          const msalResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: currentAccount
          });
          const loginResponse = await login(msalResponse.idToken);
          store.dispatch(setToken({ token: loginResponse.token }));
          originalRequest.headers.Authorization = `Bearer ${loginResponse.token}`;
          return client(originalRequest);
        }
      } catch (msalError) {
        console.error('[MSAL Refresh Failed]', msalError);
        // Trigger logout flow in App.js via "error" token
        store.dispatch(setToken({ token: "error" }));
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }

      toast.error("Session expired. Please log in again.");
      store.dispatch(setToken({ token: "error" }));
    } else if (status === 403) {
      toast.info("The application has been updated. Reloading...");
      setTimeout(() => window.location.reload(), 1500);
    } else if (status >= 500) {
      toast.error("Server error. Please try again later.");
    }

    return Promise.reject(error);
  },
);

export const login = async (msalToken) => {
  try {
    const response = await client.post('/data/login', {}, {
      headers: { Authorization: `Bearer ${msalToken}` }
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error ?? err.message);
  }
};

export const getModuleData = async () => {
  try {
    const response = await client.get('/data/module');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getPlasticData = async () => {
  try {
    const response = await client.get('/data/plastic');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getIpData = async () => {
  try {
    const response = await client.get('/data/ip');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getGAMainData = async () => {
  try {
    const response = await client.get('/data/main');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getGASubData = async () => {
  try {
    const response = await client.get('/data/sub');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getGAShipItem = async (plant, ifkdt, seqhd) => {
  try {
    const response = await client.get('/data/gashipitem', {
      params: { plant, ifkdt, seqhd }
    });
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getRTMSData = async () => {
  try {
    const response = await client.get('/data/rtms');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const vaildateToken = async () => {
  try {
    const response = await client.get('/data/vaildateToken');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};
